/**
 * Formatting utilities for Starlink (Clarus) telemetry values.
 * Pure functions with no UI dependencies — safe in table cell renderers,
 * stat cards, chart tooltips, or anywhere else.
 */

/**
 * Clarus returns numerics inconsistently — sometimes a number, sometimes a
 * string like "134.6451228800001". Anything non-numeric becomes null rather
 * than NaN, so callers get one "no value" case to handle instead of two.
 */
export const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * KAN-111: throughput arrives from Clarus **already in Mbps** — no conversion.
 *
 * This previously multiplied by 1000 on the belief the values were Gbps, which
 * rendered every speed 1000× too high: a terminal reporting 134.645 showed as
 * "134645.1 Mbps", i.e. 134 Gbps on a Starlink dish. Verified against
 * /telemetry/latest, where a busy terminal reports downlinkThroughput
 * "134.6451228800001" and prod's stored range is 0.005–134.6 — ordinary as
 * Mbps, impossible as Gbps.
 *
 * Idle terminals sit near zero, so sub-1 values are normal and want more
 * decimal places than a busy one.
 */
export const fmtThroughput = (v: number | string | null | undefined): string => {
  const mbps = toNum(v);
  if (mbps === null) return '—';
  return `${mbps.toFixed(mbps < 10 ? 2 : 1)} Mbps`;
};
